import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  ExpandMore,
  Add,
  Edit,
  Delete,
  Save,
  Cancel as CancelIcon,
  Payment,
  Receipt,
  MoneyOff,
  Schedule,
  Security,
  Timeline,
  Info,
  Warning,
  CheckCircle,
  Error,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { cancellationRefundService, globalService } from '../../services/firebase';
import { CancellationRefundData, CancellationPolicy, RefundMethod, ImportantNote } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';

// Icon options for refund methods
const iconOptions = [
  { value: 'Payment', label: 'Kredi Kartı', icon: <Payment /> },
  { value: 'Receipt', label: 'Makbuz', icon: <Receipt /> },
  { value: 'MoneyOff', label: 'Nakit', icon: <MoneyOff /> },
  { value: 'Schedule', label: 'Zaman', icon: <Schedule /> },
  { value: 'Security', label: 'Güvenlik', icon: <Security /> },
  { value: 'Timeline', label: 'Zaman Çizelgesi', icon: <Timeline /> },
  { value: 'info', label: 'Bilgi', icon: <Info /> },
  { value: 'warning', label: 'Uyarı', icon: <Warning /> },
  { value: 'success', label: 'Başarılı', icon: <CheckCircle /> },
  { value: 'error', label: 'Hata', icon: <Error /> }
];

// Color options for cancellation policies
const colorOptions = [
  { value: 'success', label: 'Yeşil (Başarılı)' },
  { value: 'info', label: 'Mavi (Bilgi)' },
  { value: 'warning', label: 'Turuncu (Uyarı)' },
  { value: 'error', label: 'Kırmızı (Hata)' }
];

// Note type options
const noteTypeOptions = [
  { value: 'info', label: 'Bilgi', icon: <Info /> },
  { value: 'warning', label: 'Uyarı', icon: <Warning /> },
  { value: 'success', label: 'Başarılı', icon: <CheckCircle /> },
  { value: 'error', label: 'Hata', icon: <Error /> }
];

const ManageCancellationRefundPage: React.FC = () => {
  const [refundData, setRefundData] = useState<CancellationRefundData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();

  // Modal states
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  
  // Edit states
  const [editingPolicy, setEditingPolicy] = useState<CancellationPolicy | null>(null);
  const [editingMethod, setEditingMethod] = useState<RefundMethod | null>(null);
  const [editingNote, setEditingNote] = useState<ImportantNote | null>(null);

  useEffect(() => {
    fetchRefundData();
  }, []);

  const fetchRefundData = async () => {
    try {
      setLoading(true);
      let data = await cancellationRefundService.getCancellationRefundData();
      
      if (!data) {
        await cancellationRefundService.initializeCancellationRefundData();
        data = await cancellationRefundService.getCancellationRefundData();
      }
      
      setRefundData(data);
    } catch (err: any) {
      setError('Veri yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (field: 'title' | 'description' | 'content', value: string) => {
    if (!refundData) return;
    const updatedData = setTranslatedData({ ...refundData }, field, selectedLang, value);
    setRefundData(updatedData);
  };
  
  const handleTranslateBasicInfo = async () => {
    if (!refundData) return;
    setSaving(true);
    try {
      let updatedData = { ...refundData };
      const sourceLang = 'tr';
      const targetLangs = ['en', 'ar'];

      // Translate title
      const titleSource = getTranslatedData(updatedData.title, sourceLang);
      if (titleSource) {
        const translations = await globalService.translateTexts({ text: titleSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'title', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'title', 'ar', translations.ar);
      }
      
      // Translate description
      const descriptionSource = getTranslatedData(updatedData.description, sourceLang);
      if (descriptionSource) {
        const translations = await globalService.translateTexts({ text: descriptionSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'description', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'description', 'ar', translations.ar);
      }

      // Translate content
      const contentSource = getTranslatedData(updatedData.content, sourceLang);
      if (contentSource) {
        const translations = await globalService.translateTexts({ text: contentSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'content', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'content', 'ar', translations.ar);
      }

      setRefundData(updatedData);
      setSuccess('Temel bilgiler çevirisi tamamlandı.');
    } catch (err) {
      setError('Temel bilgiler çevrilirken bir hata oluştu.');
      console.error('Translation error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    try {
      setSaving(true);
      setError(null);
      
      if (!refundData?.id) return;
      
      await cancellationRefundService.updateCancellationRefundData(refundData);
      setSuccess('Temel bilgiler başarıyla güncellendi!');
    } catch (err: any) {
      setError('Güncelleme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePolicy = async (policy: CancellationPolicy) => {
    try {
      setSaving(true);
      setError(null);
      
      let policies = [...(refundData?.cancellationPolicies || [])];
      
      if (editingPolicy) {
        policies = policies.map(p => p.id === policy.id ? policy : p);
      } else {
        const newPolicy = {
          ...policy,
          id: Date.now().toString(),
          order: policies.length + 1
        };
        policies.push(newPolicy);
      }
      
      const updatedData = {
        ...refundData,
        cancellationPolicies: policies
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setPolicyModalOpen(false);
      setEditingPolicy(null);
      setSuccess(editingPolicy ? 'İptal politikası güncellendi!' : 'Yeni iptal politikası eklendi!');
    } catch (err: any) {
      setError('Kaydetme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    try {
      setSaving(true);
      setError(null);
      
      const policies = refundData?.cancellationPolicies?.filter(p => p.id !== policyId) || [];
      
      const updatedData = {
        ...refundData,
        cancellationPolicies: policies
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setSuccess('İptal politikası silindi!');
    } catch (err: any) {
      setError('Silme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMethod = async (method: RefundMethod) => {
    try {
      setSaving(true);
      setError(null);
      
      let methods = [...(refundData?.refundMethods || [])];
      
      if (editingMethod) {
        methods = methods.map(m => m.id === method.id ? method : m);
      } else {
        const newMethod = {
          ...method,
          id: Date.now().toString(),
          order: methods.length + 1
        };
        methods.push(newMethod);
      }
      
      const updatedData = {
        ...refundData,
        refundMethods: methods
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setMethodModalOpen(false);
      setEditingMethod(null);
      setSuccess(editingMethod ? 'İade yöntemi güncellendi!' : 'Yeni iade yöntemi eklendi!');
    } catch (err: any) {
      setError('Kaydetme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMethod = async (methodId: string) => {
    try {
      setSaving(true);
      setError(null);
      
      const methods = refundData?.refundMethods?.filter(m => m.id !== methodId) || [];
      
      const updatedData = {
        ...refundData,
        refundMethods: methods
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setSuccess('İade yöntemi silindi!');
    } catch (err: any) {
      setError('Silme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async (note: ImportantNote) => {
    try {
      setSaving(true);
      setError(null);
      
      let notes = [...(refundData?.importantNotes || [])];
      
      if (editingNote) {
        notes = notes.map(n => n.id === note.id ? note : n);
      } else {
        const newNote = {
          ...note,
          id: Date.now().toString(),
          order: notes.length + 1
        };
        notes.push(newNote);
      }
      
      const updatedData = {
        ...refundData,
        importantNotes: notes
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setNoteModalOpen(false);
      setEditingNote(null);
      setSuccess(editingNote ? 'Not güncellendi!' : 'Yeni not eklendi!');
    } catch (err: any) {
      setError('Kaydetme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      setSaving(true);
      setError(null);
      
      const notes = refundData?.importantNotes?.filter(n => n.id !== noteId) || [];
      
      const updatedData = {
        ...refundData,
        importantNotes: notes
      } as CancellationRefundData;
      
      await cancellationRefundService.updateCancellationRefundData(updatedData);
      setRefundData(updatedData as CancellationRefundData);
      setSuccess('Not silindi!');
    } catch (err: any) {
      setError('Silme sırasında hata oluştu: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!refundData) {
    return <Typography color="error">Veri yüklenemedi.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        İptal ve İade Sayfası Yönetimi
      </Typography>
      
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <ToggleButtonGroup
        value={selectedLang}
        exclusive
        onChange={(e, newLang) => newLang && setSelectedLang(newLang)}
        aria-label="text alignment"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="tr" aria-label="left aligned">TR</ToggleButton>
        <ToggleButton value="en" aria-label="centered">EN</ToggleButton>
        <ToggleButton value="ar" aria-label="right aligned">AR</ToggleButton>
      </ToggleButtonGroup>

      {/* Basic Info Accordion */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Temel Bilgiler</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component={Paper} p={2}>
            <TextField
              label="Sayfa Başlığı"
              fullWidth
              value={getTranslatedData(refundData.title, selectedLang)}
              onChange={e => handleBasicInfoChange('title', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Kısa Açıklama"
              multiline
              rows={3}
              fullWidth
              value={getTranslatedData(refundData.description, selectedLang)}
              onChange={e => handleBasicInfoChange('description', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="İçerik"
              multiline
              rows={5}
              fullWidth
              variant="outlined"
              value={getTranslatedData(refundData.content, selectedLang)}
              onChange={e => handleBasicInfoChange('content', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                startIcon={<TranslateIcon />}
                onClick={handleTranslateBasicInfo}
                disabled={saving}
              >
                Tümünü Çevir
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveBasicInfo}
                disabled={saving}
                startIcon={<Save />}
              >
                {saving ? <CircularProgress size={24} /> : t('save')}
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Cancellation Policies Accordion */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">İptal Politikaları</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setEditingPolicy(null);
              setPolicyModalOpen(true);
            }}
            sx={{ mb: 2 }}
          >
            Yeni Politika Ekle
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Periyot</TableCell>
                  <TableCell>İade</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundData.cancellationPolicies?.map(policy => (
                  <TableRow key={policy.id}>
                    <TableCell>{getTranslatedData(policy.period, selectedLang)}</TableCell>
                    <TableCell>{getTranslatedData(policy.refund, selectedLang)}</TableCell>
                    <TableCell><Chip label={policy.isActive ? 'Aktif' : 'Pasif'} color={policy.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <IconButton onClick={() => {
                        setEditingPolicy(policy);
                        setPolicyModalOpen(true);
                      }}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeletePolicy(policy.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
      
      {/* Refund Methods Accordion */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">İade Yöntemleri</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setEditingMethod(null);
              setMethodModalOpen(true);
            }}
            sx={{ mb: 2 }}
          >
            Yeni Yöntem Ekle
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Yöntem</TableCell>
                  <TableCell>Süre</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundData.refundMethods?.map(method => (
                  <TableRow key={method.id}>
                    <TableCell>{getTranslatedData(method.method, selectedLang)}</TableCell>
                    <TableCell>{getTranslatedData(method.time, selectedLang)}</TableCell>
                    <TableCell><Chip label={method.isActive ? 'Aktif' : 'Pasif'} color={method.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <IconButton onClick={() => {
                        setEditingMethod(method);
                        setMethodModalOpen(true);
                      }}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteMethod(method.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Important Notes Accordion */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Önemli Notlar</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setEditingNote(null);
              setNoteModalOpen(true);
            }}
            sx={{ mb: 2 }}
          >
            Yeni Not Ekle
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundData.importantNotes?.map(note => (
                  <TableRow key={note.id}>
                    <TableCell>{getTranslatedData(note.title, selectedLang)}</TableCell>
                    <TableCell>{note.type}</TableCell>
                    <TableCell><Chip label={note.isActive ? 'Aktif' : 'Pasif'} color={note.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <IconButton onClick={() => {
                        setEditingNote(note);
                        setNoteModalOpen(true);
                      }}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteNote(note.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <PolicyModal 
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        onSave={handleSavePolicy}
        policy={editingPolicy}
        saving={saving}
        selectedLang={selectedLang}
      />
      <MethodModal 
        open={methodModalOpen}
        onClose={() => setMethodModalOpen(false)}
        onSave={handleSaveMethod}
        method={editingMethod}
        saving={saving}
        selectedLang={selectedLang}
      />
      <NoteModal
        open={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        onSave={handleSaveNote}
        note={editingNote}
        saving={saving}
        selectedLang={selectedLang}
      />
    </Box>
  );
};

const PolicyModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (policy: CancellationPolicy) => void;
  policy: CancellationPolicy | null;
  saving: boolean;
  selectedLang: string;
}> = ({ open, onClose, onSave, policy, saving, selectedLang }) => {
  const { t } = useTranslation();
  const [currentPolicy, setCurrentPolicy] = useState<CancellationPolicy | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const newPolicyTemplate: CancellationPolicy = {
      id: '',
      period: { tr: '', en: '', ar: '' },
      refund: { tr: '', en: '', ar: '' },
      conditions: { tr: '', en: '', ar: '' },
      color: 'info',
      order: 0,
      isActive: true
    };
    setCurrentPolicy(policy || newPolicyTemplate);
  }, [policy, open]);

  const handleTranslatableChange = (field: 'period' | 'refund' | 'conditions', value: string) => {
    setCurrentPolicy(prev => prev ? setTranslatedData({ ...prev }, field, selectedLang, value) : null);
  };
  
  const handleNonTranslatableChange = (field: 'color' | 'isActive', value: any) => {
    setCurrentPolicy(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleTranslate = async () => {
    if (!currentPolicy) return;
    setIsTranslating(true);
    try {
      let updatedData = { ...currentPolicy };
      const sourceLang = 'tr';
      const targetLangs = ['en', 'ar'];
      const fieldsToTranslate: (keyof Pick<CancellationPolicy, 'period' | 'refund' | 'conditions'>)[] = ['period', 'refund', 'conditions'];

      for (const field of fieldsToTranslate) {
        const sourceText = getTranslatedData(updatedData[field], sourceLang);
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang, targetLangs });
          Object.keys(translations).forEach(lang => {
            updatedData = setTranslatedData(updatedData, field as string, lang, translations[lang]);
          });
        }
      }
      setCurrentPolicy(updatedData);
    } catch (err) {
      console.error("Translation failed", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = () => {
    onSave(currentPolicy as CancellationPolicy);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{policy ? 'Politikayı Düzenle' : 'Yeni Politika Ekle'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Periyot"
          fullWidth
          value={getTranslatedData(currentPolicy?.period, selectedLang)}
          onChange={(e) => handleTranslatableChange('period', e.target.value)}
          margin="normal"
        />
        <TextField
          label="İade Yüzdesi/Miktarı"
          fullWidth
          value={getTranslatedData(currentPolicy?.refund, selectedLang)}
          onChange={(e) => handleTranslatableChange('refund', e.target.value)}
          margin="normal"
        />
        <TextField
          label="Koşullar"
          fullWidth
          multiline
          rows={4}
          value={getTranslatedData(currentPolicy?.conditions, selectedLang)}
          onChange={(e) => handleTranslatableChange('conditions', e.target.value)}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Renk</InputLabel>
          <Select
            value={currentPolicy?.color || 'info'}
            label="Renk"
            onChange={(e) => handleNonTranslatableChange('color', e.target.value as CancellationPolicy['color'])}
          >
            {colorOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={currentPolicy?.isActive || false} onChange={e => handleNonTranslatableChange('isActive', e.target.checked)} />}
          label={t('active')}
        />
      </DialogContent>
      <DialogActions>
        <Button startIcon={<TranslateIcon />} onClick={handleTranslate} disabled={saving || isTranslating}>
          {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving || isTranslating}>
          {saving ? <CircularProgress size={24} /> : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MethodModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (method: RefundMethod) => void;
  method: RefundMethod | null;
  saving: boolean;
  selectedLang: string;
}> = ({ open, onClose, onSave, method, saving, selectedLang }) => {
  const { t } = useTranslation();
  const [currentMethod, setCurrentMethod] = useState<RefundMethod | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const newMethodTemplate: RefundMethod = {
      id: '',
      method: { tr: '', en: '', ar: '' },
      time: { tr: '', en: '', ar: '' },
      icon: 'Payment',
      order: 0,
      isActive: true
    };
    setCurrentMethod(method || newMethodTemplate);
  }, [method, open]);

  const handleTranslatableChange = (field: 'method' | 'time', value: string) => {
    setCurrentMethod(prev => prev ? setTranslatedData({ ...prev }, field, selectedLang, value) : null);
  };

  const handleNonTranslatableChange = (field: 'icon' | 'isActive', value: any) => {
    setCurrentMethod(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleTranslate = async () => {
    if (!currentMethod) return;
    setIsTranslating(true);
    try {
      let updatedData = { ...currentMethod };
      const sourceLang = 'tr';
      const targetLangs = ['en', 'ar'];
      const fieldsToTranslate: (keyof Pick<RefundMethod, 'method' | 'time'>)[] = ['method', 'time'];

      for (const field of fieldsToTranslate) {
        const sourceText = getTranslatedData(updatedData[field], sourceLang);
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang, targetLangs });
          Object.keys(translations).forEach(lang => {
            updatedData = setTranslatedData(updatedData, field as string, lang, translations[lang]);
          });
        }
      }
      setCurrentMethod(updatedData);
    } catch (err) {
      console.error("Translation failed", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = () => {
    onSave(currentMethod as RefundMethod);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{method ? 'Yöntemi Düzenle' : 'Yeni Yöntem Ekle'}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            label="Yöntem"
            fullWidth
            value={getTranslatedData(currentMethod?.method, selectedLang)}
            onChange={(e) => handleTranslatableChange('method', e.target.value)}
            margin="normal"
          />
          <TextField
            label="Süre"
            fullWidth
            value={getTranslatedData(currentMethod?.time, selectedLang)}
            onChange={(e) => handleTranslatableChange('time', e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>İkon</InputLabel>
            <Select
              value={currentMethod?.icon || 'Payment'}
              label="İkon"
              onChange={(e) => handleNonTranslatableChange('icon', e.target.value)}
              renderValue={(value) => {
                const selected = iconOptions.find(opt => opt.value === value);
                return <Box sx={{ display: 'flex', alignItems: 'center' }}>{selected?.icon}{selected?.label}</Box>;
              }}
            >
              {iconOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={currentMethod?.isActive || false} onChange={e => handleNonTranslatableChange('isActive', e.target.checked)} />}
            label={t('active')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<TranslateIcon />} onClick={handleTranslate} disabled={saving || isTranslating}>
          {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving || isTranslating}>
          {saving ? <CircularProgress size={24} /> : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NoteModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (note: ImportantNote) => void;
  note: ImportantNote | null;
  saving: boolean;
  selectedLang: string;
}> = ({ open, onClose, onSave, note, saving, selectedLang }) => {
  const { t } = useTranslation();
  const [currentNote, setCurrentNote] = useState<ImportantNote | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const newNoteTemplate: ImportantNote = {
      id: '',
      title: { tr: '', en: '', ar: '' },
      content: { tr: '', en: '', ar: '' },
      type: 'info',
      order: 0,
      isActive: true
    };
    setCurrentNote(note || newNoteTemplate);
  }, [note, open]);

  const handleTranslatableChange = (field: 'title' | 'content', value: string) => {
    setCurrentNote(prev => prev ? setTranslatedData({ ...prev }, field, selectedLang, value) : null);
  };
  
  const handleNonTranslatableChange = (field: 'type' | 'isActive', value: any) => {
    setCurrentNote(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleTranslate = async () => {
    if (!currentNote) return;
    setIsTranslating(true);
    try {
      let updatedData = { ...currentNote };
      const sourceLang = 'tr';
      const targetLangs = ['en', 'ar'];
      const fieldsToTranslate: (keyof Pick<ImportantNote, 'title' | 'content'>)[] = ['title', 'content'];

      for (const field of fieldsToTranslate) {
        const sourceText = getTranslatedData(updatedData[field], sourceLang);
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang, targetLangs });
          Object.keys(translations).forEach(lang => {
            updatedData = setTranslatedData(updatedData, field as string, lang, translations[lang]);
          });
        }
      }
      setCurrentNote(updatedData);
    } catch (err) {
      console.error("Translation failed", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = () => {
    onSave(currentNote as ImportantNote);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{note ? 'Notu Düzenle' : 'Yeni Not Ekle'}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            label="Başlık"
            fullWidth
            value={getTranslatedData(currentNote?.title, selectedLang)}
            onChange={(e) => handleTranslatableChange('title', e.target.value)}
            margin="normal"
          />
          <TextField
            label="İçerik"
            fullWidth
            multiline
            rows={4}
            value={getTranslatedData(currentNote?.content, selectedLang)}
            onChange={(e) => handleTranslatableChange('content', e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Tip</InputLabel>
            <Select
              value={currentNote?.type || 'info'}
              label="Tip"
              onChange={(e) => handleNonTranslatableChange('type', e.target.value as ImportantNote['type'])}
              renderValue={(value) => {
                const selected = noteTypeOptions.find(opt => opt.value === value);
                return <Box sx={{ display: 'flex', alignItems: 'center' }}>{selected?.icon}{selected?.label}</Box>;
              }}
            >
              {noteTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={currentNote?.isActive || false} onChange={e => handleNonTranslatableChange('isActive', e.target.checked)} />}
            label={t('active')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<TranslateIcon />} onClick={handleTranslate} disabled={saving || isTranslating}>
          {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving || isTranslating}>
          {saving ? <CircularProgress size={24} /> : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageCancellationRefundPage; 